import React from 'react'
import { LogOut, User } from 'lucide-react'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useEmbedSession } from '@/contexts/embed-session-context'

type UserAccountMenuProps = {
  userInitials?: string
  username?: string | null
  onLogout?: () => void
}

// Generate initials from username
function resolveUserInitials(userInitials?: string, username?: string | null): string {
  return (
    userInitials ||
    (username
      ? username
          .split(/[\s._-]/)
          .map((part) => part[0])
          .join('')
          .toUpperCase()
          .slice(0, 2)
      : 'U')
  )
}

/**
 * Avatar button with the signed-in user's name, Profile (disabled) and Log out. Embedded in a host
 * app (`useEmbedSession`), Log out is left out: the host owns the session.
 */
export const UserAccountMenu: React.FC<UserAccountMenuProps> = ({
  userInitials,
  username,
  onLogout,
}) => {
  const initials = resolveUserInitials(userInitials, username)
  const hideLogout = useEmbedSession() !== null

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="relative h-8 w-8 rounded-full">
          <Avatar className="size-[var(--menubar-avatar-size)]">
            <AvatarFallback className="bg-secondary">{initials}</AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56" align="end" forceMount>
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium leading-none">{username || 'User'}</p>
            <p className="text-xs leading-none text-muted-foreground">Signed in</p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem disabled>
          <User className="mr-2 h-4 w-4" />
          <span>Profile</span>
        </DropdownMenuItem>
        {hideLogout ? null : (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onLogout} className="text-red-600 focus:text-red-600">
              <LogOut className="mr-2 h-4 w-4" />
              <span>Log out</span>
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
